<template>
  <div
    v-if="shouldMessageBeVisible"
    role="region"
    :class="['message-bar js-banner', isTypeClass, hasCloseButtonClass]">
    <div class="message-bar__container">
      <oie-base-icon
        :icon="icon"
        size="l"></oie-base-icon>
      <span class="message-bar__message-type-sr-only">{{ messageTypeText }}</span>
      <template v-if="isDefaultSlotPopulated">
        <slot></slot>
      </template>

      <span v-if="!isDefaultSlotPopulated && mq('to-small') && mobileText" class="message-bar__text" v-html="mobileText"></span>

      <span v-else-if="!isDefaultSlotPopulated && mq('small')" class="message-bar__text" v-html="text"></span>

      <a v-if="linkUrl && linkText" class="message-bar__link" :href="linkUrl">{{ linkText }}<oie-base-icon
        v-if="linkIcon"
        :icon="linkIcon"
        size="s"
        class="message-bar__link-icon"></oie-base-icon></a>

      <slot name="close">
        <button
          v-if="useCloseButton"
          class="message-bar__dismiss-actions"
          :aria-label="tStatic('dismissMessage')"
          @click="removeMessage()">
          <template v-if="mq('medium') && closeButtonText">
            {{ closeButtonText }}
          </template>

          <oie-use-svg svg="close" class="message-bar__x-icon"></oie-use-svg>
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import EventBus from '@/core/event-bus'

export default {
  props: {
    id: {
      type: String,
      default: null
    },
    text: {
      type: String,
      default: null
    },
    mobileText: {
      type: String,
      default: null
    },
    linkUrl: {
      type: String,
      default: null
    },
    linkText: {
      type: String,
      default: null
    },
    linkIcon: {
      type: String,
      default: null
    },
    isType: {
      type: String,
      default: 'info'
    },
    customIcon: {
      type: String,
      default: null
    },
    useCloseButton: {
      type: Boolean,
      default: true
    },
    closeButtonText: {
      type: String,
      default: null
    }
  },
  data: () => ({
    isMessageVisible: false
  }),
  computed: {
    ...mapGetters('ui', ['isMessageShow']),
    isTypeClass() {
      return `message-bar--${this.isType}`
    },
    hasCloseButtonClass() {
      return this.useCloseButton ? 'message-bar--has-close-button' : null
    },
    shouldMessageBeVisible: {
      get: function() {
        return this.isMessageVisible
      },
      set: function(newValue) {
        this.isMessageVisible = newValue
      }
    },
    isDefaultSlotPopulated() {
      return this.$slots.default !== undefined
    },
    icon() {
      if (this.customIcon) {
        return this.customIcon
      }
      switch(this.isType) {
        case 'error':
          return 'warning'

        case 'success':
          return 'checkmark-outline'
        default:
          return 'information'
      }
    },
    messageTypeText() {
      return this.tStatic(`${this.isType}MessageText`)
    }
  },
  mounted() {
    this.isMessageVisible = this.id !== null ? this.isMessageShow(this.id) : true
  },
  methods: {
    ...mapActions('ui', ['dismissMessage']),
    removeMessage() {
      if (this.id) {
        this.dismissMessage(this.id)
      }

      this.shouldMessageBeVisible = false

      setTimeout(() => {
        EventBus.$emit('dismiss-message')
      }, 200)
    }
  }
}
</script>

<style lang="scss">
.message-bar--info {
  background-color: color(message-warning);
  color: color(message-warning-color);

  &:not(:last-child) {
    border-bottom: 1px solid darken(color(message-warning), 40%);
  }
}

.message-bar--error {
  background-color: color(message-error);
  color: color(message-error-color);

  &:not(:last-child) {
    border-bottom: 1px solid darken(color(message-error), 50%);
  }
}

.message-bar--success {
  background-color: color(message-success);
  color: color(message-success-color);

  &:not(:last-child) {
    border-bottom: 1px solid darken(color(message-success), 50%);
  }
}

.message-bar--additional {
  background-color: color(message-additional);
  color: color(message-additional-color);
}

.message-bar--transparent-error {
  background-color: transparent;
  color: color(text);

  & .svg {
    color: color(message-warning);
  }
}

.message-bar__container {
  @include container;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 2.75rem;
  padding-top: measure(quarter);
  padding-bottom: measure(quarter);
}

.message-bar__container > svg {
  width: 1.625rem;
  height: 1.625rem;
  margin-right: measure(quarter);
  flex-shrink: 0;
}

.message-bar__message-type-sr-only {
  position:absolute;
  left:-10000px;
  top:auto;
  width:1px;
  height:1px;
  overflow:hidden;
}

.message-bar__text {
  max-width: 48.75rem;
  color: #fff;
}

.message-bar__text > p {
  margin: 0;
  padding: 0;
}

.message-bar--has-close-button .message-bar__text {
  padding-right: measure(half);
}

.message-bar__dismiss-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
  padding: 0;
  background-color: transparent;
  color: inherit;
  border: none;

  &:hover {
    cursor: pointer;
  }
}

.message-bar__x-icon {
  width: 1.625rem;
  height: 1.625rem;
  margin-left: measure(eighth);
}

  .message-bar__link {
    color: #FFFFFF;
    margin-left: 0.6rem;
    text-decoration: underline
  }

  .message-bar__link:hover {
    text-decoration-style: wavy;
  }

  .message-bar__link-icon {
    vertical-align: middle;
    margin-left: .4rem;
  }

</style>
